import React, { useState, useEffect } from 'react';
import { Plus, X, Upload, Building2, User, Calendar, DollarSign, Download, Eye, Save } from 'lucide-react';
import { smartGeneratePDF } from '../utils/pdfGenerator';
import AdSense from './AdSense';

// ========================================
// CONSTANTS & UTILITIES (Centralized)
// ========================================

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$'
};

const STYLES = {
  input: "w-full border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none p-4 transition-all duration-200",
  textarea: "w-full border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none p-4 transition-all duration-200",
  select: "w-full border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none p-4 transition-all duration-200",
  itemInput: "w-full border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none p-3 transition-all duration-200",
  primaryButton: "bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105",
  saveButton: "bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105",
  addButton: "w-full border-2 border-dashed border-indigo-200 rounded-2xl p-6 text-indigo-600 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-all duration-200 flex items-center justify-center group",
  removeButton: "text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-xl transition-all duration-200",
  section: "bg-white rounded-2xl p-6 shadow-lg border",
  sectionTitle: "text-lg font-semibold text-gray-800 mb-6 flex items-center",
  label: "block text-sm font-medium text-gray-700 mb-2"
};

// Utility functions
const formatCurrency = (amount, currency = 'USD') => {
  return `${CURRENCY_SYMBOLS[currency] || '$'}${amount.toFixed(2)}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// ========================================
// INVOICE FORM COMPONENT
// ========================================

const InvoiceForm = ({
  invoiceData,
  updateInvoiceData,
  updateNestedData,
  updateItem,
  addItem,
  removeItem,
  updateTaxRate,
  onSave,
  isSaved
}) => {
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateInvoiceData('logo', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-8">
      {/* Save Actions */}
      <div className={STYLES.section}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Invoice Actions</h3>
          <button onClick={onSave} className={STYLES.saveButton}>
            <Save className="w-4 h-4" />
            <span>{isSaved ? 'Update Invoice' : 'Save Invoice'}</span>
          </button>
        </div>
        {isSaved && (
          <p className="text-sm text-green-600 mt-2 flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Invoice saved successfully
          </p>
        )}
      </div>

      {/* AdSense */}
      <div className={STYLES.section}>
        <AdSense adSlot="1234567892" adFormat="rectangle" />
      </div>

      {/* Logo Upload */}
      <div className={STYLES.section}>
        <h3 className={STYLES.sectionTitle}>
          <Upload className="w-5 h-5 mr-2 text-indigo-600" />
          Company Logo
        </h3>
        <div className="border-2 border-dashed border-indigo-200 rounded-2xl p-8 text-center hover:border-indigo-400 transition-all duration-200 bg-gradient-to-br from-indigo-50 to-purple-50">
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
            id="logo-upload"
          />
          <label htmlFor="logo-upload" className="cursor-pointer">
            {invoiceData.logo ? (
              <img src={invoiceData.logo} alt="Company Logo" className="max-h-20 mx-auto mb-2 rounded-lg shadow-md" />
            ) : (
              <Upload className="w-12 h-12 mx-auto text-indigo-400 mb-2" />
            )}
            <p className="text-indigo-600 font-medium">
              {invoiceData.logo ? 'Change Logo' : 'Upload Logo'}
            </p>
            <p className="text-sm text-gray-500 mt-1">PNG, JPG up to 2MB</p>
          </label>
        </div>
      </div>

      {/* Company Information */}
      <div className={STYLES.section}>
        <h3 className={STYLES.sectionTitle}>
          <Building2 className="w-5 h-5 mr-2 text-indigo-600" />
          Your Business Information
        </h3>
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Business Name"
            value={invoiceData.companyInfo.name}
            onChange={(e) => updateNestedData('companyInfo', 'name', e.target.value)}
            className={STYLES.input}
          />
          <textarea
            placeholder="Business Address"
            value={invoiceData.companyInfo.address}
            onChange={(e) => updateNestedData('companyInfo', 'address', e.target.value)}
            rows="3"
            className={STYLES.textarea}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="email"
              placeholder="Email Address"
              value={invoiceData.companyInfo.email}
              onChange={(e) => updateNestedData('companyInfo', 'email', e.target.value)}
              className={STYLES.input}
            />
            <input
              type="tel"
              placeholder="Phone Number"
              value={invoiceData.companyInfo.phone}
              onChange={(e) => updateNestedData('companyInfo', 'phone', e.target.value)}
              className={STYLES.input}
            />
          </div>
        </div>
      </div>

      {/* Client Information */}
      <div className={STYLES.section}>
        <h3 className={STYLES.sectionTitle}>
          <User className="w-5 h-5 mr-2 text-indigo-600" />
          Bill To
        </h3>
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Client Name"
            value={invoiceData.clientInfo.name}
            onChange={(e) => updateNestedData('clientInfo', 'name', e.target.value)}
            className={STYLES.input}
          />
          <textarea
            placeholder="Client Address"
            value={invoiceData.clientInfo.address}
            onChange={(e) => updateNestedData('clientInfo', 'address', e.target.value)}
            rows="3"
            className={STYLES.textarea}
          />
          <input
            type="email"
            placeholder="Client Email"
            value={invoiceData.clientInfo.email}
            onChange={(e) => updateNestedData('clientInfo', 'email', e.target.value)}
            className={STYLES.input}
          />
        </div>
      </div>

      {/* Invoice Details */}
      <div className={STYLES.section}>
        <h3 className={STYLES.sectionTitle}>
          <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
          Invoice Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={STYLES.label}>Invoice Number</label>
            <input
              type="text"
              value={invoiceData.invoiceDetails.number}
              onChange={(e) => updateNestedData('invoiceDetails', 'number', e.target.value)}
              className={STYLES.input}
            />
          </div>
          <div>
            <label className={STYLES.label}>Currency</label>
            <select
              value={invoiceData.currency}
              onChange={(e) => updateInvoiceData('currency', e.target.value)}
              className={STYLES.select}
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD (C$)</option>
              <option value="AUD">AUD (A$)</option>
            </select>
          </div>
          <div>
            <label className={STYLES.label}>Invoice Date</label>
            <input
              type="date"
              value={invoiceData.invoiceDetails.date}
              onChange={(e) => updateNestedData('invoiceDetails', 'date', e.target.value)}
              className={STYLES.input}
            />
          </div>
          <div>
            <label className={STYLES.label}>Due Date</label>
            <input
              type="date"
              value={invoiceData.invoiceDetails.dueDate}
              onChange={(e) => updateNestedData('invoiceDetails', 'dueDate', e.target.value)}
              className={STYLES.input}
            />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className={STYLES.section}>
        <h3 className={STYLES.sectionTitle}>
          <DollarSign className="w-5 h-5 mr-2 text-indigo-600" />
          Invoice Items
        </h3>
        <div className="space-y-4">
          {invoiceData.items.map((item, index) => (
            <div key={item.id} className="border border-gray-200 rounded-2xl p-6 bg-gradient-to-r from-gray-50 to-indigo-50">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-end">
                <div className="md:col-span-5">
                  <label className={STYLES.label}>Description</label>
                  <input
                    type="text"
                    placeholder="Item description"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    className={STYLES.itemInput}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={STYLES.label}>Qty</label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    className={STYLES.itemInput}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={STYLES.label}>Rate</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.rate}
                    onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                    className={STYLES.itemInput}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={STYLES.label}>Amount</label>
                  <div className="text-lg font-semibold text-gray-800 py-2 px-3 bg-white rounded-xl border">
                    {formatCurrency(item.amount, invoiceData.currency)}
                  </div>
                </div>
                <div className="md:col-span-1 flex justify-end">
                  {invoiceData.items.length > 1 && (
                    <button onClick={() => removeItem(index)} className={STYLES.removeButton}>
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          <button onClick={addItem} className={STYLES.addButton}>
            <Plus className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
            Add Line Item
          </button>
        </div>
      </div>

      {/* Tax Settings */}
      <div className={STYLES.section}>
        <h3 className="text-lg font-semibold text-gray-800 mb-6">Tax & Totals</h3>
        <div className="flex items-center justify-between">
          <span className="text-gray-700 font-medium">Tax Rate (%)</span>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={invoiceData.totals.taxRate}
              onChange={(e) => updateTaxRate(parseFloat(e.target.value) || 0)}
              className="w-20 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none text-right p-3 transition-all duration-200"
            />
            <span className="text-gray-600">%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================================
// INVOICE PREVIEW COMPONENT
// ========================================

const InvoicePreview = ({ invoiceData }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const result = await smartGeneratePDF(invoiceData);
      if (result.success) {
        console.log(`PDF generated: ${result.filename}`);
      } else {
        alert('Download failed. Please try again.');
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Preview Header */}
      <div className="flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg border">
        <div className="flex items-center space-x-3">
          <Eye className="w-6 h-6 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-800">Live Preview</h3>
        </div>
        <button
          onClick={handleDownload}
          disabled={isGenerating}
          className={`${STYLES.primaryButton} disabled:opacity-50 disabled:transform-none`}
        >
          <Download className="w-4 h-4" />
          <span>{isGenerating ? 'Generating...' : 'Download PDF'}</span>
        </button>
      </div>

      {/* AdSense */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border">
        <AdSense adSlot="1234567895" adFormat="rectangle" />
      </div>

      {/* Invoice Preview */}
      <div id="invoice-preview" className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg min-h-[800px]">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start mb-8 space-y-4 md:space-y-0">
          <div className="flex-1">
            {invoiceData.logo && (
              <img 
                src={invoiceData.logo} 
                alt="Company Logo" 
                className="max-h-16 mb-4 rounded-lg shadow-sm"
              />
            )}
            <div className="text-sm text-gray-600">
              <div className="font-semibold text-lg text-gray-900 mb-2">
                {invoiceData.companyInfo.name || 'Your Business Name'}
              </div>
              {invoiceData.companyInfo.address && (
                <div className="whitespace-pre-line mb-2">{invoiceData.companyInfo.address}</div>
              )}
              {invoiceData.companyInfo.email && (
                <div className="mb-1">{invoiceData.companyInfo.email}</div>
              )}
              {invoiceData.companyInfo.phone && (
                <div>{invoiceData.companyInfo.phone}</div>
              )}
            </div>
          </div>
          
          <div className="text-left md:text-right w-full md:w-auto">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">INVOICE</h1>
            <div className="text-sm text-gray-600 space-y-1">
              <div><span className="font-medium">Invoice #:</span> {invoiceData.invoiceDetails.number}</div>
              <div><span className="font-medium">Date:</span> {formatDate(invoiceData.invoiceDetails.date)}</div>
              {invoiceData.invoiceDetails.dueDate && (
                <div><span className="font-medium">Due Date:</span> {formatDate(invoiceData.invoiceDetails.dueDate)}</div>
              )}
              {invoiceData.invoiceDetails.poNumber && (
                <div><span className="font-medium">PO #:</span> {invoiceData.invoiceDetails.poNumber}</div>
              )}
            </div>
          </div>
        </div>

        {/* Bill To Section */}
        <div className="mb-8">
          <div className="font-semibold text-gray-900 mb-2">Bill To:</div>
          <div className="text-sm text-gray-600">
            <div className="font-medium text-gray-900 mb-1">
              {invoiceData.clientInfo.name || 'Client Name'}
            </div>
            {invoiceData.clientInfo.address && (
              <div className="whitespace-pre-line mb-1">{invoiceData.clientInfo.address}</div>
            )}
            {invoiceData.clientInfo.email && (
              <div>{invoiceData.clientInfo.email}</div>
            )}
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-8">
          <div className="invoice-table-container overflow-x-auto">
            <table className="invoice-table w-full min-w-[600px]">
              <thead>
                <tr className="border-b-2 border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Description</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700 w-20">Qty</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700 w-24">Rate</th>
                  <th className="text-right py-4 px-4 text-sm font-semibold text-gray-700 w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.items.map((item, index) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4 text-sm text-gray-800">
                      {item.description || `Item ${index + 1}`}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600 text-center">
                      {item.quantity}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600 text-center">
                      {formatCurrency(item.rate, invoiceData.currency)}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-800 text-right font-medium">
                      {formatCurrency(item.amount, invoiceData.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals Section */}
        <div className="flex justify-end">
          <div className="totals-section w-full md:w-80 space-y-3 bg-gradient-to-r from-gray-50 to-indigo-50 p-6 rounded-2xl">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal:</span>
              <span className="font-medium">{formatCurrency(invoiceData.totals.subtotal, invoiceData.currency)}</span>
            </div>
            
            {invoiceData.totals.taxRate > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Tax ({invoiceData.totals.taxRate}%):</span>
                <span className="font-medium">{formatCurrency(invoiceData.totals.taxAmount, invoiceData.currency)}</span>
              </div>
            )}
            
            <div className="border-t-2 border-indigo-200 pt-3">
              <div className="flex justify-between text-xl font-bold text-gray-900">
                <span>Total:</span>
                <span className="text-indigo-600">{formatCurrency(invoiceData.totals.total, invoiceData.currency)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t-2 border-gray-200">
          <div className="text-center text-sm text-gray-500">
            <p className="font-medium text-gray-700 mb-2">Thank you for your business!</p>
            <p>Payment terms: Net 30 days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================================
// MAIN INVOICE CREATOR COMPONENT
// ========================================

const InvoiceCreator = ({ invoiceData, setInvoiceData, onSaveInvoice, isSaved }) => {
  // Calculate totals whenever items or tax rate changes
  useEffect(() => {
    const subtotal = invoiceData.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    const taxAmount = (subtotal * invoiceData.totals.taxRate) / 100;
    const total = subtotal + taxAmount;

    setInvoiceData(prev => ({
      ...prev,
      totals: {
        ...prev.totals,
        subtotal,
        taxAmount,
        total
      }
    }));
  }, [invoiceData.items, invoiceData.totals.taxRate, setInvoiceData]);

  // Data update functions
  const updateInvoiceData = (field, value) => {
    setInvoiceData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedData = (section, field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].rate;
    }

    setInvoiceData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0
    };
    setInvoiceData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const removeItem = (index) => {
    if (invoiceData.items.length > 1) {
      const updatedItems = invoiceData.items.filter((_, i) => i !== index);
      setInvoiceData(prev => ({ ...prev, items: updatedItems }));
    }
  };

  const updateTaxRate = (rate) => {
    setInvoiceData(prev => ({
      ...prev,
      totals: { ...prev.totals, taxRate: rate }
    }));
  };

  return (
    <>
      {/* Mobile Responsive CSS */}
      <style jsx>{`
        @media (max-width: 1279px) {
          .invoice-creator-container { display: block !important; }
          .invoice-form-section, .invoice-preview-section {
            padding: 0 !important;
            max-height: none !important;
            overflow-y: visible !important;
            background: white !important;
          }
          .invoice-form-wrapper, .invoice-preview-wrapper { padding: 1rem; }
        }
        @media (min-width: 1280px) {
          .invoice-creator-container {
            display: grid !important;
            grid-template-columns: 1fr 1fr;
            min-height: 800px;
          }
          .invoice-form-section {
            padding: 2rem;
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(4px);
            overflow-y: auto;
            max-height: 800px;
          }
          .invoice-preview-section {
            padding: 2rem;
            background: white;
            overflow-y: auto;
            max-height: 800px;
          }
          .invoice-form-wrapper, .invoice-preview-wrapper { padding: 0; }
          .invoice-form-section::-webkit-scrollbar,
          .invoice-preview-section::-webkit-scrollbar { width: 8px; }
          .invoice-form-section::-webkit-scrollbar-track,
          .invoice-preview-section::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          .invoice-form-section::-webkit-scrollbar-thumb,
          .invoice-preview-section::-webkit-scrollbar-thumb {
            background: #6366f1;
            border-radius: 4px;
          }
        }
        .invoice-table-container { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .invoice-table { min-width: 600px; width: 100%; }
        @media (max-width: 768px) {
          .invoice-table th, .invoice-table td { padding: 0.75rem 0.5rem; font-size: 0.875rem; }
          .invoice-table th:first-child, .invoice-table td:first-child { min-width: 200px; }
          .totals-section { width: 100% !important; }
        }
      `}</style>

      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 py-8 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Create Professional Invoice</h1>
            <p className="text-indigo-200 text-lg max-w-2xl mx-auto">Fill in the details and watch your invoice generate in real-time</p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-white/10">
            <div className="invoice-creator-container">
              {/* Form Section */}
              <div className="invoice-form-section">
                <div className="invoice-form-wrapper">
                  <InvoiceForm
                    invoiceData={invoiceData}
                    updateInvoiceData={updateInvoiceData}
                    updateNestedData={updateNestedData}
                    updateItem={updateItem}
                    addItem={addItem}
                    removeItem={removeItem}
                    updateTaxRate={updateTaxRate}
                    onSave={onSaveInvoice}
                    isSaved={isSaved}
                  />
                </div>
              </div>
              
              {/* Preview Section */}
              <div className="invoice-preview-section">
                <div className="invoice-preview-wrapper">
                  <InvoicePreview invoiceData={invoiceData} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceCreator;